import classnames from 'classnames';
import React, { useState } from 'react';

import Drawer, {
  DrawerItem,
  DrawerItemContent,
  DrawerItemTitle
} from 'dpl/common/components/Drawer';
import Icon from 'dpl/common/components/Icon';
import { HELP_EMAIL, HELP_NUMBER } from 'dpl/shared/constants/breeder_app';
import { HELP_ARTICLE_BUYER_PAYMENTS_PATH } from 'dpl/shared/constants/urls';

export interface CheckoutFaq {
  question: string;
  answer: string;
}

interface CheckoutHelpSectionProps {
  faqs: CheckoutFaq[];
  className?: string;
}

function SectionToggle({
  title,
  isOpen,
  onClick
}: {
  title: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex items-center gap-4 primary fw-medium font-14"
      onClick={onClick}
      aria-expanded={isOpen}
    >
      {title}
      <Icon
        name={isOpen ? 'fetch-chevron-up' : 'fetch-chevron-down'}
        width="14px"
        height="14px"
      />
    </button>
  );
}

const CONTACT_BUTTON_CLASSES =
  'flex-auto flex items-center justify-center gap-8 br2 ba b--gray-300 bg-white stone-700 pv3 font-16';

export default function CheckoutHelpSection({
  faqs,
  className = ''
}: CheckoutHelpSectionProps) {
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <section
      className={classnames(
        'CheckoutHelpSection pt6 bt b--gray-300',
        className
      )}
      data-test-id="checkout-help-section"
    >
      <SectionToggle
        title="Frequently asked questions"
        isOpen={isFaqOpen}
        onClick={() => setIsFaqOpen(!isFaqOpen)}
      />
      {isFaqOpen && (
        <div className="mt2">
          <Drawer scrollToTop={false}>
            {faqs.map(faq => (
              <DrawerItem
                key={faq.question}
                itemKey={faq.question}
                className="bb b--gray-300"
              >
                <DrawerItemTitle
                  className="pv4 tl"
                  iconCloseName="fetch-plus"
                  iconOpenName="fetch-minus"
                  iconClassName="ml4 stone-700"
                  iconSize="20px"
                >
                  <span className="font-16 fw-medium">{faq.question}</span>
                </DrawerItemTitle>
                <DrawerItemContent className="pb4 font-16 secondary">
                  <p>{faq.answer}</p>
                </DrawerItemContent>
              </DrawerItem>
            ))}
          </Drawer>
          <a
            href={HELP_ARTICLE_BUYER_PAYMENTS_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-8 br3 bg-gray-200 ph4 pv3 mt4 stone-700"
          >
            <span className="flex-auto">
              <span className="db font-14 fw-medium">
                More payment questions?
              </span>
              <span className="db font-14 secondary">
                Browse the Good Dog Help Center
              </span>
            </span>
            <Icon name="fetch-arrow-right" width="20px" height="20px" />
          </a>
        </div>
      )}

      <div className="mt4">
        <SectionToggle
          title="Contact us"
          isOpen={isContactOpen}
          onClick={() => setIsContactOpen(!isContactOpen)}
        />
        {isContactOpen && (
          <div className="mt2">
            <p className="font-16">
              Need help? Our team is available Monday–Friday, 9am–6pm ET.
            </p>
            <div className="flex gap-8 mt3">
              <a href={`tel:${HELP_NUMBER}`} className={CONTACT_BUTTON_CLASSES}>
                <Icon name="fetch-phone" width="20px" height="20px" />
                Call us
              </a>
              <a
                href={`mailto:${HELP_EMAIL}`}
                className={CONTACT_BUTTON_CLASSES}
              >
                <Icon name="fetch-mail" width="20px" height="20px" />
                Email us
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
